import React, { useEffect, useRef, useState } from 'react';

// Global flag to track FPD loading state
window._fpdLoadingState = window._fpdLoadingState || {
  loaded: false,
  loading: false,
  error: null
};

const SimpleLabelDesigner = ({ onDesignChange }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initFabricCanvas = () => {
      if (!window.fabric) {
        console.log('🔄 Loading Fabric.js...');
        
        // Load Fabric.js
        const fabricScript = document.createElement('script');
        fabricScript.src = '/fpd/js/fabric.min.js';
        fabricScript.onload = () => {
          console.log('✅ Fabric.js loaded');
          createCanvas();
        };
        fabricScript.onerror = () => {
          setError('Errore nel caricamento di Fabric.js');
        };
        document.body.appendChild(fabricScript);
      } else {
        createCanvas();
      }
    };

    const createCanvas = () => {
      if (!canvasRef.current || fabricCanvasRef.current) return;

      try {
        // Create Fabric.js canvas
        const canvas = new window.fabric.Canvas(canvasRef.current, {
          width: 400,
          height: 300,
          backgroundColor: '#ffffff',
          selection: true
        });

        fabricCanvasRef.current = canvas;

        // Add initial text
        const text = new window.fabric.Text('Il Tuo Amaro', {
          left: 100,
          top: 50,
          fontSize: 24,
          fontFamily: 'Arial',
          fill: '#8B4513',
          editable: true
        });
        canvas.add(text);

        // Add sample elements
        const subtitle = new window.fabric.Text('Liquore Artigianale', {
          left: 100,
          top: 90,
          fontSize: 14,
          fontFamily: 'Arial',
          fill: '#CD853F'
        });
        canvas.add(subtitle);

        // Create a simple decorative element
        const rect = new window.fabric.Rect({
          left: 50,
          top: 150,
          width: 300,
          height: 2,
          fill: '#D2691E'
        });
        canvas.add(rect);

        // Event listeners
        canvas.on('object:modified', handleCanvasChange);
        canvas.on('object:added', handleCanvasChange);
        canvas.on('object:removed', handleCanvasChange);

        setIsReady(true);
        console.log('✅ Simple Label Designer ready!');

      } catch (error) {
        console.error('❌ Canvas creation error:', error);
        setError('Errore nella creazione del canvas');
      }
    };

    const handleCanvasChange = () => {
      if (!fabricCanvasRef.current) return;

      try {
        const dataURL = fabricCanvasRef.current.toDataURL({
          format: 'png',
          quality: 0.8,
          multiplier: 2
        });

        const canvasData = JSON.stringify(fabricCanvasRef.current.toJSON());

        if (onDesignChange) {
          onDesignChange({
            imageDataURL: dataURL,
            designData: canvasData,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('❌ Export error:', error);
      }
    };

    initFabricCanvas();

    // Cleanup
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, []);

  const addText = () => {
    if (!fabricCanvasRef.current) return;

    const text = new window.fabric.Text('Nuovo Testo', {
      left: Math.random() * 200 + 50,
      top: Math.random() * 150 + 50,
      fontSize: 18,
      fontFamily: 'Arial',
      fill: '#8B4513'
    });
    
    fabricCanvasRef.current.add(text);
    fabricCanvasRef.current.setActiveObject(text);
  };

  const addShape = () => {
    if (!fabricCanvasRef.current) return;

    const circle = new window.fabric.Circle({
      left: Math.random() * 200 + 50,
      top: Math.random() * 150 + 50,
      radius: 20,
      fill: '#D2691E',
      stroke: '#8B4513',
      strokeWidth: 2
    });
    
    fabricCanvasRef.current.add(circle);
    fabricCanvasRef.current.setActiveObject(circle);
  };

  const clearCanvas = () => {
    if (!fabricCanvasRef.current) return;
    fabricCanvasRef.current.clear();
    fabricCanvasRef.current.backgroundColor = '#ffffff';
    fabricCanvasRef.current.renderAll();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Errore Designer</div>
          <div className="text-red-500 text-sm">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Ricarica Pagina
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="label-designer-container">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Designer Etichetta Personalizzata (Semplice)
        </h3>
        <p className="text-sm text-gray-600">
          Crea la tua etichetta con testo personalizzato e forme
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Tools Panel */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Strumenti</h4>
          
          <button
            onClick={addText}
            disabled={!isReady}
            className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
          >
            📝 Aggiungi Testo
          </button>
          
          <button
            onClick={addShape}
            disabled={!isReady}
            className="w-full px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-300 transition-colors"
          >
            🔶 Aggiungi Forma
          </button>
          
          <button
            onClick={clearCanvas}
            disabled={!isReady}
            className="w-full px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-gray-300 transition-colors"
          >
            🗑️ Cancella Tutto
          </button>
          
          {isReady && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-xs text-green-700">
              ✅ Designer pronto!<br/>
              Clicca sugli elementi per modificarli
            </div>
          )}
        </div>

        {/* Canvas Area */}
        <div className="lg:col-span-3">
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white relative">
            <canvas 
              ref={canvasRef}
              className="block mx-auto"
            />
            
            {!isReady && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <div className="text-gray-600">Caricamento designer...</div>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-2 text-xs text-gray-500 text-center">
            Dimensioni: 400x300px - Clicca e trascina per modificare gli elementi
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleLabelDesigner;